/**
 * The «Не прошёл ЕВИ» virtual display status — T.Z204 (owner 25.09).
 *
 * The owner's ask: «when deadline evi passed we need to automatically set status Не
 * прошёл ЕВИ for those who were in Получил ссылку state and make red pill and possible
 * to filter by this status in the Статус field». Nothing is "set": like «Провалил ЕВИ»
 * (T.Z113/T.Z143, `evi-failed.ts`) this is a DERIVED status computed at read — the
 * sweeps write nothing and no stored column ever holds the token. The difference is the
 * anchor: «Провалил ЕВИ» is anchored in a stored FACT (the child vanished from the ЕВИ
 * queue), «Не прошёл ЕВИ» is anchored in the CLOCK — a child who got the link and let
 * the ЕВИ deadline lapse without passing.
 *
 * The rule, in one sentence: **a row displays «Не прошёл ЕВИ» when its stored status is
 * LINK_RECEIVED, the ЕВИ deadline (link + 10 days) is in the PAST, and the child never
 * passed.** The 10 days are the SAME constant the «Дедлайн ЕВИ» column has always used
 * (the api's `apps/api/src/exports/columns.ts` `eviDeadlineAt`, the web's
 * `applications-model.ts` `eviDeadline` — both now delegate to
 * {@link EVI_DEADLINE_DAYS} here), so the register can never show a red «Не прошёл ЕВИ»
 * on a row whose «Дедлайн ЕВИ» cell still reads as time left.
 *
 * PRECEDENCE (the display fold lives in `evi-failed.ts` `displayStatusOf`): «Провалил
 * ЕВИ» outranks. A vanished child is the stronger fact — a row that carries the fact
 * keeps reading «Провалил ЕВИ» even when its clock has also lapsed, exactly as it did
 * before this status existed. This status therefore overlays a PLAIN LINK_RECEIVED row
 * only, and the `?status=` predicate carries the same `NOT flagged` guard so a row
 * never answers two virtual tokens at once.
 *
 * PURE and clock-injected on purpose: {@link isEviNotPassed} is a function of (status,
 * linkReceivedAt, eviPassedAt, now) and nothing else, so the api's SQL predicate, the
 * export's cell and the web's badge derive IDENTICALLY — the same one-instant rule the
 * `?ageGroup=` bucket (v1.9.48) set.
 */
/**
 * The owner-approved ЕВИ rule (T.F1): an applicant has 10 days from the link to pass
 * the ЕВИ. Hoisted here (v1.9.66, T.Z204) from its two app-side copies so the deadline
 * COLUMN, the deadline CELL and this status can never drift apart.
 */
export const EVI_DEADLINE_DAYS = 10;
/** The deadline span in milliseconds — `link_received_at` + this = the ЕВИ deadline. */
export const EVI_DEADLINE_SPAN_MS = EVI_DEADLINE_DAYS * 24 * 60 * 60 * 1000;
/**
 * The VIRTUAL status token, the owner's own words (T.Z204). Not an `AppStatus` member
 * (the six-value enum stays frozen, docs/05 §4.1) and never a stored
 * `applications.status` value — it exists on the wire as a `?status=` value (#3 and
 * #18 alike, the v1.9.36 `EVI_FAILED` precedent) and in the UI's display layer.
 */
export const EVI_NOT_PASSED_STATUS_TOKEN = 'EVI_NOT_PASSED';
/** The label every surface prints: the register's pill, the select's option, the file's cell. */
export const EVI_NOT_PASSED_LABEL = 'Не прошёл ЕВИ';
/**
 * The ЕВИ deadline instant of a row, or `null` when the clock is not running.
 *
 * Only a LINK_RECEIVED row has one — the exact rule the api's `eviDeadlineAt` and the
 * web's `eviDeadline` have always applied: passed (EVI_PASSED) or judged
 * (REJECTED / AWAITING_INFO / NO_DATA) and the clock is not the thing a manager acts
 * on; without `linkReceivedAt` there is no start of it. An unparseable date is no date
 * (the web's «не дата» case) — `null`, never a guessed deadline.
 */
export function eviDeadlineInstant(input) {
    if (input.status !== 'LINK_RECEIVED')
        return null;
    const received = input.linkReceivedAt instanceof Date
        ? input.linkReceivedAt
        : input.linkReceivedAt === null || input.linkReceivedAt === undefined
            ? null
            : new Date(input.linkReceivedAt);
    if (received === null || Number.isNaN(received.getTime()))
        return null;
    return new Date(received.getTime() + EVI_DEADLINE_SPAN_MS);
}
/**
 * THE fold (T.Z204): is this row's display status «Не прошёл ЕВИ»? `true` exactly when
 * the stored status is LINK_RECEIVED, the deadline instant ({@link eviDeadlineInstant})
 * is strictly in the past (`now > deadline` — the web's `overdue` reading, so a row
 * whose deadline is THIS INSTANT still counts as having time), and `evi_passed_at` is
 * null. A row that carries the «Провалил ЕВИ» fact answers `false` HERE BY CONSTRUCTION
 * only when the caller says so — the guard lives in the SQL predicate and in
 * `displayStatusOf`, because the fact outranks the clock and this function must stay a
 * pure clock question.
 */
export function isEviNotPassed(input, now) {
    const deadline = eviDeadlineInstant(input);
    if (deadline === null)
        return false;
    if (now.getTime() <= deadline.getTime())
        return false;
    if (input.eviPassedAt !== null && input.eviPassedAt !== undefined)
        return false;
    return true;
}
//# sourceMappingURL=evi-not-passed.js.map